/**
 * treatrxAdmin.service.ts — Admin → Integrations → TreatRx.
 *
 * Two independent surfaces, matching the two tabs:
 *  - authorization → aa_accounts_ayva (the per-account OAuth client)
 *  - providers     → aa_ayva_doctors  (user ↔ TreatRx doctor links)
 *
 * Distinct from `prescriptions.service.ts` (the patient-side read context) and
 * `treatrx.client.ts` (direct browser calls to the TreatRx middleware). Those
 * two consume what this one configures.
 */
import { apiClient } from './apiClient';

// ─── Authorization ───────────────────────────────────────────────────────────

export interface TreatRxCredentials {
  /** Not secret — it goes into the TreatRx consent URL. Returned in full. */
  clientId: string;
  /** Mask sentinel when a secret is stored, '' otherwise. Never the real value. */
  clientSecret: string;
  /** Masked tail, e.g. '••••abcd', so an admin can tell which secret is stored. */
  secretHint: string;
  hasSecret: boolean;
}

export interface TreatRxAuthorization {
  configured: boolean;
  credentials: TreatRxCredentials;
  /** >1 means duplicate aa_accounts_ayva rows exist; only the newest is read. */
  rowCount: number;
}

export interface TreatRxAuthorizationInput {
  clientId?: string;
  /** Omit or send '' to keep the stored secret. */
  clientSecret?: string;
}

export function getTreatRxAuthorization(): Promise<TreatRxAuthorization> {
  return apiClient
    .get<{ data: TreatRxAuthorization }>('/treatrx/authorization')
    .then((r) => r.data.data);
}

export function updateTreatRxAuthorization(
  payload: TreatRxAuthorizationInput,
): Promise<TreatRxAuthorization> {
  return apiClient
    .put<{ data: TreatRxAuthorization }>('/treatrx/authorization', payload)
    .then((r) => r.data.data);
}

// ─── Providers ───────────────────────────────────────────────────────────────

export interface TreatRxProviderRow {
  id: number;
  userId: number;
  ayvaDoctorID: string;
  providerName: string;
  role: string;
  /** The linked user is still active. Inactive links keep listing so they can be removed. */
  userActive: boolean;
  /** The linked users row no longer exists at all. */
  orphanedUser: boolean;
  /** The doctor has completed the TreatRx consent flow (a refresh token is stored). */
  authorized: boolean;
  refreshTokenExpiration: string;
}

export interface TreatRxProviderInput {
  userId: number;
  ayvaDoctorID: string;
}

export function listTreatRxProviders(): Promise<TreatRxProviderRow[]> {
  return apiClient
    .get<{ data: TreatRxProviderRow[] }>('/treatrx/providers')
    .then((r) => r.data.data);
}

export function createTreatRxProvider(payload: TreatRxProviderInput): Promise<{ id: number }> {
  return apiClient
    .post<{ data: { id: number } }>('/treatrx/providers', payload)
    .then((r) => r.data.data);
}

export function updateTreatRxProvider(
  id: number,
  payload: TreatRxProviderInput,
): Promise<{ success: boolean; credentialsCleared: boolean }> {
  return apiClient
    .put<{ data: { success: boolean; credentialsCleared: boolean } }>(
      `/treatrx/providers/${id}`,
      payload,
    )
    .then((r) => r.data.data);
}

export function deleteTreatRxProvider(id: number): Promise<void> {
  return apiClient.delete(`/treatrx/providers/${id}`).then(() => undefined);
}

/** Blank the stored grant so the doctor must re-authorize with TreatRx. */
export function resetTreatRxProviderCredentials(id: number): Promise<{ success: boolean }> {
  return apiClient
    .post<{ data: { success: boolean } }>(`/treatrx/providers/${id}/reset-credentials`)
    .then((r) => r.data.data);
}
